import { createSimpleContext } from "@openanalyst-ai/ui/context"
import type { AsyncStorage, SyncStorage } from "@solid-primitives/storage"
import type { Accessor } from "solid-js"
import type {
  MiniChatAlwaysOnTopInput,
  MiniChatMainRouteInput,
  MiniChatOpenInput,
  MiniChatOpenResult,
} from "@/control-room/mini-chat"
import type { UpdaterPlatform } from "@/updater"
import type { WslServersPlatform } from "@/wsl/types"
import { ServerConnection } from "./server"
import type { DesktopMenuAction } from "@/desktop-menu"

type PickerPaths = string | string[] | null
type OpenDirectoryPickerOptions = { title?: string; multiple?: boolean; defaultPath?: string }
type OpenFilePickerOptions = { title?: string; multiple?: boolean; accept?: string[]; extensions?: string[] }
type SaveFilePickerOptions = { title?: string; defaultPath?: string }
type UpdateInfo = { updateAvailable: boolean; version?: string }
export type SmartworkRuntimeHandle = {
  handle: string
  handleDisplay?: string | null
  accessRole?: "CREATOR" | "OPERATOR" | "OWNER" | null
}
export type SmartworkAuthStartResult = { attemptId: string; authorizeUrl: string; expiresAt?: string | null }
export type SmartworkAuthStatus = {
  connected: boolean
  smartworkUrl?: string | null
  apiBaseUrl?: string | null
  expiresAt?: number | null
  userEmail?: string | null
  bridgeConnected?: boolean
  bridgeExpiresAt?: number | null
  bridgeDesktopId?: string | null
  bridgeDesktopIdSource?: "bridge_token" | "hosted_exchange" | "none" | null
}
export type SmartworkPasswordlessCodeDelivery = {
  destination?: string | null
  deliveryMedium?: string | null
}
export type SmartworkPasswordlessStartResult = {
  connected: boolean
  challenge?: string | null
  session?: string | null
  expiresAt?: number | null
  userEmail?: string | null
  codeDelivery?: SmartworkPasswordlessCodeDelivery | null
}
export type OpenAnalystDedicatedAuthTokenPayload = {
  accessToken?: string | null
  access_token?: string | null
  expiresIn?: number | null
  expires_in?: number | null
  expiresAt?: number | null
  expires_at?: number | null
  tokenType?: string | null
  token_type?: string | null
  userEmail?: string | null
  user_email?: string | null
}
export type OpenAnalystAuthTokenPayload = {
  accessToken?: string | null
  access_token?: string | null
  idToken?: string | null
  id_token?: string | null
  expiresIn?: number | null
  expires_in?: number | null
  expiresAt?: number | null
  expires_at?: number | null
  tokenType?: string | null
  token_type?: string | null
  userEmail?: string | null
  user_email?: string | null
  openAnalyst?: OpenAnalystDedicatedAuthTokenPayload | null
}
export type OpenAnalystPasswordlessStartResult = OpenAnalystAuthTokenPayload & SmartworkPasswordlessStartResult
export type OpenAnalystPasswordlessConfirmResult = OpenAnalystAuthTokenPayload & { ok?: boolean }
export type OpenAnalystPasswordlessConfirmInput =
  | {
      email: string
      code: string
      session: string
      smartworkUrl?: string | null
      apiBaseUrl?: string | null
      authUrl?: string | null
    }
  | {
      authIntentId: string
      code: string
      smartworkUrl?: string | null
      apiBaseUrl?: string | null
      authUrl?: string | null
    }
export type SmartworkRuntimeResult = {
  state: "ready" | "configured" | "needs_auth" | "needs_handle" | "provisioning_pending" | "error" | string
  connected: boolean
  smartworkUrl?: string | null
  apiBaseUrl?: string | null
  smartworkToken?: string | null
  handle?: string | null
  runtimeId?: string | null
  workspaceId?: string | null
  projectName?: string | null
  workspacePath?: string | null
  matchesWorkspace?: boolean | null
  localRuntimeUrl?: string | null
  bridgeDesktopId?: string | null
  bridgeDesktopIdSource?: "bridge_token" | "hosted_exchange" | "none" | null
  handles: SmartworkRuntimeHandle[]
  message?: string | null
  errorCode?: string | null
}

export type CrmCloudCutoverInput = {
  projectDir: string
  projectId: string
  schemaVersion: number
}

export type CrmCloudCutoverResult =
  | { status: "cancelled" }
  | {
      status: "completed"
      sessionId: string
      backupDirectory: string
      enabledAt: string
      manifestHash: string
      totalRecords: number
    }

export type SmartworkRuntimeStatusInput = {
  workspacePath?: string | null
}
export type TenxMemoryRuntimeContextRefreshResult = {
  state: "ready" | "needs_auth" | "needs_handle" | "error" | string
  connected: boolean
  smartworkUrl?: string | null
  apiBaseUrl?: string | null
  handle?: string | null
  workspaceId?: string | null
  projectName?: string | null
  workspacePath?: string | null
  handles: SmartworkRuntimeHandle[]
  message?: string | null
}
export type DiagnosticField = { key: string; value: string }
export type DiagnosticLogInput = {
  level?: "error" | "warn" | "info" | "debug" | "trace" | string | null
  source?:
    | "frontend"
    | "desktop"
    | "openanalyst-server"
    | "smartwork"
    | "smartwork-server"
    | "smartwork-orchestrator"
    | "openanalyst-router"
    | string
    | null
  target?: string | null
  tags?: string[] | null
  message: string
  fields?: DiagnosticField[] | null
}
export type DiagnosticLogEntry = {
  cursor: number
  timestamp: string
  level: string
  source: string
  target: string
  tags: string[]
  message: string
  fields: DiagnosticField[]
}
export type DiagnosticsStatus = {
  enabled: boolean
  logDir: string
  currentFile?: string | null
  viewerUrl?: string | null
  retainedDays: number
  retainedBytes: number
}
export type DiagnosticsTailInput = {
  cursor?: number | null
  limit?: number | null
  levels?: string[] | null
  sources?: string[] | null
  tags?: string[] | null
  query?: string | null
}
export type DiagnosticsTailResult = {
  entries: DiagnosticLogEntry[]
  nextCursor: number
  logPath?: string | null
}
export type FatalRendererErrorLog = {
  message: string
  stack?: string
  url?: string
  userAgent?: string
  timestamp?: string
}

export type Platform = {
  /** Platform discriminator */
  platform: "web" | "desktop"

  /** Desktop OS */
  os?: "macos" | "windows" | "linux"

  /** App version */
  version?: string

  /** Start desktop app services whose work must continue without an open app iframe. */
  activateAppSidecars?(projectDir: string): Promise<void>

  /** Import the current local CRM snapshot and irreversibly switch this project to cloud authority. */
  crmCloudCutover?(input: CrmCloudCutoverInput): Promise<CrmCloudCutoverResult>

  /** Open a URL in the default browser */
  openLink(url: string): void

  /**
   * Open a URL in an in-app window (desktop only). Returns a window id used to
   * close it, or null if unavailable (web falls back to openLink).
   */
  openIntegrationWindow?(url: string): Promise<number | null>
  /** Close an in-app window opened by openIntegrationWindow (desktop only). */
  closeIntegrationWindow?(id: number): Promise<boolean>
  /** Notified when an in-app integration window is closed (desktop only). Returns an unsubscribe fn. */
  onIntegrationWindowClosed?(callback: (id: number) => void): () => void

  /** Open a local path in a local app (desktop only) */
  openPath?(path: string, app?: string): Promise<void>

  /** Restart the app  */
  restart(): Promise<void>

  /** Gracefully quit the desktop app and its child processes */
  quit?(): Promise<void> | void

  /** Navigate back in history */
  back(): void

  /** Navigate forward in history */
  forward(): void

  /** Send a system notification (optional deep link) */
  notify(title: string, description?: string, href?: string): Promise<void>

  /** Open directory picker dialog (native on desktop, server-backed on web) */
  openDirectoryPickerDialog?(opts?: OpenDirectoryPickerOptions): Promise<PickerPaths>

  /** Create a whitelisted local starter project on desktop. */
  createStarterProject?(input: {
    templateId:
      | "ai-marketing-workspace"
      | "email-revenue-system"
      | "ecommerce-ads-analysis"
      | "google-ads-growth-review"
      | "facebook-ads-performance-review"
      | "paid-ads-creative-strategy"
      | "funnel-conversion-analysis"
      | "lead-generation-system"
      | "seo-authority-growth-system"
      | "youtube-social-growth-system"
      | "analytics-measurement-readiness"
      | "openanalyst-shopify-manager"
      | "job-research"
    rootDirectory?: string
    /** Onboarding-v3 sets this so the new project is auto-bound to the
     *  user's claimed handle + chosen workspace. */
    binding?: { handle: string; workspace: string }
  }): Promise<{ directory: string }>

  /** Create an EMPTY local project folder — no starter template copied.
   *  Desktop only; absent on web, where the new-project dialog falls back to
   *  browse-only. `allowAppIDs`, when non-empty, restricts which bundled apps
   *  auto-materialize into the project. */
  createProjectDirectory?(input: {
    name?: string
    rootDirectory?: string
    allowAppIDs?: string[]
  }): Promise<{ directory: string }>

  /** The bundled apps a new project can be limited to. Desktop only. */
  listBundledApps?(): Promise<{ id: string; name: string; icon?: string }[]>

  /** Open native file picker dialog (desktop only) */
  openFilePickerDialog?(opts?: OpenFilePickerOptions): Promise<PickerPaths>

  /** Save file picker dialog (desktop only) */
  saveFilePickerDialog?(opts?: SaveFilePickerOptions): Promise<string | null>

  /** Open or focus the compact desktop Mini Chat window. */
  openMiniChat?(input: MiniChatOpenInput): Promise<MiniChatOpenResult>

  /** Toggle always-on-top for the compact desktop Mini Chat window. */
  setMiniChatAlwaysOnTop?(input: MiniChatAlwaysOnTopInput): Promise<MiniChatOpenResult>

  /** Focus the main desktop window and navigate it to a route. */
  openMainWindowRoute?(input: MiniChatMainRouteInput): Promise<MiniChatOpenResult>

  /** Open a new desktop window and navigate it to a route. */
  openRouteWindow?(input: MiniChatMainRouteInput): Promise<MiniChatOpenResult>

  /** Storage mechanism, defaults to localStorage */
  storage?: (name?: string) => SyncStorage | AsyncStorage

  /** Check for a downloadable desktop update */
  checkUpdate?(): Promise<UpdateInfo>

  /** Install the downloaded update using the platform restart flow */
  updateAndRestart?(): Promise<void>

  /** Desktop update state and actions */
  updater?: UpdaterPlatform

  /** Fetch override */
  fetch?: typeof fetch

  /** Raw Smartwork control-plane fetch for desktop-only service calls that must bypass webview CORS. */
  smartworkControlPlaneFetch?: typeof fetch

  /** Get the configured default server URL (platform-specific) */
  getDefaultServer?(): Promise<ServerConnection.Key | null>

  /** Set the default server URL to use on app startup (platform-specific) */
  setDefaultServer?(url: ServerConnection.Key | null): Promise<void> | void

  /** Get the configured WSL integration (desktop only) */
  getWslEnabled?(): Promise<boolean>

  /** Set the configured WSL integration (desktop only) */
  setWslEnabled?(config: boolean): Promise<void> | void

  /** Manage WSL-backed desktop servers (desktop only) */
  wslServers?: WslServersPlatform

  /** Get the preferred display backend (desktop only) */
  getDisplayBackend?(): Promise<DisplayBackend | null> | DisplayBackend | null

  /** Set the preferred display backend (desktop only) */
  setDisplayBackend?(backend: DisplayBackend): Promise<void>

  /** Parse markdown to HTML using native parser (desktop only, returns unprocessed code blocks) */
  parseMarkdown?(markdown: string): Promise<string>

  /** Webview zoom level (desktop only) */
  webviewZoom?: Accessor<number>

  /** Get whether native pinch zoom is enabled (desktop only) */
  getPinchZoomEnabled?(): Promise<boolean> | boolean

  /** Set whether native pinch zoom is enabled (desktop only) */
  setPinchZoomEnabled?(enabled: boolean): Promise<void> | void

  /** Run a native desktop menu action (desktop only) */
  runDesktopMenuAction?(action: DesktopMenuAction): Promise<void> | void

  /** Check if an editor app exists (desktop only) */
  checkAppExists?(appName: string): Promise<boolean>

  /** Read image from clipboard (desktop only) */
  readClipboardImage?(): Promise<File | null>

  /** Start the OpenAnalyst desktop OAuth flow for Smartwork service calls. */
  smartworkAuthStart?(input?: { smartworkUrl?: string | null; apiBaseUrl?: string | null }): Promise<SmartworkAuthStartResult>

  /** Start Smartwork passwordless OTP auth inside the desktop app. */
  smartworkPasswordlessStart?(input: {
    email: string
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
  }): Promise<SmartworkPasswordlessStartResult>

  /** Confirm Smartwork passwordless OTP auth inside the desktop app. */
  smartworkPasswordlessConfirm?(input: {
    email: string
    code: string
    session: string
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
  }): Promise<SmartworkAuthStatus>

  /** Start OpenAnalyst passwordless OTP auth through the native desktop broker. */
  openAnalystPasswordlessStart?(input: {
    email: string
    intent?: "signin" | "signup"
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
    authUrl?: string | null
  }): Promise<OpenAnalystPasswordlessStartResult>

  /** Confirm OpenAnalyst passwordless OTP auth through the native desktop broker. */
  openAnalystPasswordlessConfirm?(input: OpenAnalystPasswordlessConfirmInput): Promise<OpenAnalystPasswordlessConfirmResult>

  /** Restore or refresh the native OpenAnalyst auth session without exposing refresh tokens to JS. */
  setDevicePermissions?(input: { camera?: boolean; microphone?: boolean }): Promise<{ camera: boolean; microphone: boolean }>
  openAnalystAuthRestore?(): Promise<OpenAnalystAuthTokenPayload | null>

  /** Refresh the native OpenAnalyst auth session without browser fetch or JS refresh-token access. */
  openAnalystAuthRefresh?(): Promise<OpenAnalystAuthTokenPayload | null>

  /** Clear the native OpenAnalyst auth session. */
  openAnalystAuthLogout?(): Promise<void>

  /** Return redacted Smartwork auth status. */
  smartworkAuthStatus?(): Promise<SmartworkAuthStatus>

  /** Redeem a Smartwork OpenAnalyst auth callback deep link. */
  smartworkAuthRedeem?(input: {
    url?: string | null
    attemptId?: string | null
    exchangeToken?: string | null
    deviceProof?: string | null
    handle?: string | null
    projectId?: string | null
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
  }): Promise<SmartworkAuthStatus>

  /** Return redacted Smartwork runtime status. */
  smartworkRuntimeStatus?(input?: SmartworkRuntimeStatusInput): Promise<SmartworkRuntimeResult>

  /** Publish or refresh the 10Xmemory runtime context for the active desktop workspace. */
  tenxMemoryRuntimeContextRefresh?(input: {
    workspacePath: string
    projectName?: string | null
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
    handle?: string | null
  }): Promise<TenxMemoryRuntimeContextRefreshResult>

  /** Register/reuse Smartwork runtime and start the local Smartwork host. */
  smartworkRuntimeBootstrap?(input: {
    workspacePath: string
    projectName?: string | null
    smartworkUrl?: string | null
    apiBaseUrl?: string | null
    handle?: string | null
    openAnalystToken?: string | null
    forceHandleSelection?: boolean
    rotateRuntimeToken?: boolean
  }): Promise<SmartworkRuntimeResult>

  /** List local OA project folders bound to the given openanalyst.com handle. Desktop
   *  only — web platform returns []. Used by the smartwork view so the project
   *  picker can show every handle-bound folder, not just the ones already
   *  registered in the local OA layout. */
  listHandleProjects?(input: {
    handle: string
  }): Promise<Array<{ id: string; handle: string; directory: string; label: string }>>

  /** Rotate the Smartwork runtime token and restart/reuse the local host. */
  smartworkRuntimeRotate?(input?: { workspacePath?: string | null }): Promise<SmartworkRuntimeResult>

  /** Stop the OpenAnalyst-owned Smartwork runtime process. */
  smartworkRuntimeStop?(): Promise<boolean>

  /** Clear local Smartwork auth/runtime state. */
  smartworkRuntimeReset?(): Promise<void>

  /** Write a structured diagnostics entry (desktop persists, web no-ops). */
  diagnosticsLog?(input: DiagnosticLogInput): Promise<void>

  /** Return redacted diagnostics status. */
  diagnosticsStatus?(): Promise<DiagnosticsStatus>

  /** Start/reuse the external diagnostics viewer and open it in the default browser. */
  diagnosticsOpenViewer?(): Promise<DiagnosticsStatus>

  /** Open the persisted diagnostics folder. */
  diagnosticsOpenDirectory?(): Promise<void>

  /** Tail persisted diagnostics entries. */
  diagnosticsTail?(input?: DiagnosticsTailInput): Promise<DiagnosticsTailResult>

  /** Request attention on the OS dock/taskbar (Tauri-macOS only; no-op elsewhere) */
  bounceDock?(): Promise<void> | void

  /** Trigger the Dock icon activation animation programmatically (Tauri-macOS only) */
  animateDockIcon?(): Promise<void> | void
}

export type DisplayBackend = "auto" | "wayland"

export const { use: usePlatform, provider: PlatformProvider } = createSimpleContext({
  name: "Platform",
  init: (props: { value: Platform }) => {
    return props.value
  },
})
